// 096 -- Cashback section on school detail page. Visible to school admins
// + super admins only. Renders balance card + recent accrual list.
import { useEffect, useState } from "react";
import type { SchoolCashbackResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

interface Props {
  schoolId: string;
  hasAccess: boolean;
}

export function SchoolCashbackSection({ schoolId, hasAccess }: Props) {
  const [data, setData] = useState<SchoolCashbackResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    api.getSchoolCashback(schoolId)
      .then(setData)
      // Non-admins see 403 -- silently hide.
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [schoolId, hasAccess]);

  if (loading || !data) return null;
  if (data.accruals.length === 0 && data.balance_cents === 0) return null;

  return (
    <Card data-region="school-cashback">
      <CardHeader><CardTitle>Cashback</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div>
          <p className="text-xs uppercase text-muted-foreground">School balance</p>
          <p className="text-2xl font-semibold" data-field="balance">
            {fmtMoney(data.balance_cents, data.currency)}
          </p>
        </div>
        {data.accruals.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="pb-2">Date</th>
                  <th className="pb-2">Provider</th>
                  <th className="pb-2 text-right">School share</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.accruals.slice(0, 20).map((a) => (
                  <tr key={a.id} data-row={a.id}>
                    <td className="py-2">{fmtDate(a.reported_at)}</td>
                    <td className="py-2">{a.provider_name}</td>
                    <td className="py-2 text-right font-medium">{fmtMoney(a.school_cents, a.currency)}</td>
                    <td className="py-2"><span data-status={a.status} className="rounded-full bg-muted px-2 py-0.5 text-xs">{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
